/**
 * User Preferences Store — Named Selectors
 *
 * Provides referentially stable selector functions for useUserPreferencesStore.
 * Import these instead of declaring inline `(s) => s.foo` selectors in screens
 * to avoid unnecessary re-renders and reduce duplication.
 *
 * NOTE: UserPreferencesState is not exported from userPreferencesStore.ts, so we
 * derive the type via ReturnType<typeof useUserPreferencesStore.getState>.
 */

import { useUserPreferencesStore } from './userPreferencesStore';

type UserPreferencesState = ReturnType<typeof useUserPreferencesStore.getState>;

// ── Profile ──────────────────────────────────────────────────────────

export const selectDisplayName = (s: UserPreferencesState) => s.displayName;

// ── Voice ────────────────────────────────────────────────────────────

export const selectVoiceExercisesEnabled = (s: UserPreferencesState) => s.voiceExercisesEnabled;

// ── Course preferences ───────────────────────────────────────────────

export const selectActiveCourseId = (s: UserPreferencesState) => s.activeCourseId;
export const selectCoursePreferences = (s: UserPreferencesState) => s.coursePreferences;
export const selectMaxCourses = (s: UserPreferencesState) => s.maxCourses;

// ── Derived locale fields ────────────────────────────────────────────

export const selectSourceLocale = (s: UserPreferencesState) => s.sourceLocale;
export const selectTargetLocale = (s: UserPreferencesState) => s.targetLocale;
export const selectLessonLanguage = (s: UserPreferencesState) => s.lessonLanguage;
export const selectCountry = (s: UserPreferencesState) => s.country;
export const selectResolvedCourse = (s: UserPreferencesState) => s.resolvedCourse;
export const selectTargetSttLocale = (s: UserPreferencesState) => s.resolvedCourse.targetLocale.speech.azure?.sttLocale ?? '';
export const selectTargetPronunciationFeatures = (s: UserPreferencesState) => s.resolvedCourse.targetLocale.speech.azure?.pronunciationFeatures ?? null;

// ── App state ────────────────────────────────────────────────────────

export const selectOnboardingCompleted = (s: UserPreferencesState) => s.onboardingCompleted;

// ── Developer / Feature flags ────────────────────────────────────────

export const selectDebugContextSnapshots = (s: UserPreferencesState) => s.debugContextSnapshots;
export const selectLessonFeedbackEnabled = (s: UserPreferencesState) => s.lessonFeedbackEnabled;

// ── Loading / Error ──────────────────────────────────────────────────

export const selectPreferencesIsLoading = (s: UserPreferencesState) => s.isLoading;
export const selectPreferencesError = (s: UserPreferencesState) => s.error;

// ── Actions: Profile ─────────────────────────────────────────────────

export const selectFetchProfile = (s: UserPreferencesState) => s.fetchProfile;
export const selectUpdateDisplayName = (s: UserPreferencesState) => s.updateDisplayName;

// ── Actions: Language preferences ────────────────────────────────────

export const selectSaveLanguagePreferences = (s: UserPreferencesState) => s.saveLanguagePreferences;
export const selectSwitchActiveCourse = (s: UserPreferencesState) => s.switchActiveCourse;
export const selectRemoveCourse = (s: UserPreferencesState) => s.removeCourse;

// ── Actions: Feature toggles ─────────────────────────────────────────

export const selectUpdateVoicePreferences = (s: UserPreferencesState) => s.updateVoicePreferences;
export const selectUpdateDebugContextSnapshots = (s: UserPreferencesState) => s.updateDebugContextSnapshots;
export const selectUpdateLessonFeedbackEnabled = (s: UserPreferencesState) => s.updateLessonFeedbackEnabled;

// ── Actions: Onboarding ──────────────────────────────────────────────

export const selectCompleteOnboarding = (s: UserPreferencesState) => s.completeOnboarding;
export const selectLoadFromCache = (s: UserPreferencesState) => s.loadFromCache;

// ── Actions: Cleanup ─────────────────────────────────────────────────

export const selectClearPreferencesError = (s: UserPreferencesState) => s.clearError;
